import RAPIER from "@dimforge/rapier3d-compat";
import type { World as SceneWorld } from "./scene";

// Shelf-body construction. The shelf is one dynamic compound rigid
// body so the cat can knock it over: each shelf board, the side
// supports, and the under-shelf filler hang off a single rigid body.
// CCD is on so the cat at run speed doesn't tunnel through. Mass is
// left to Rapier's auto-compute (sum of collider masses);
// friction/restitution match the original static shelf.

const SHELF_DEPTH = 1.6;
const SHELF_THICKNESS = 0.12;

export interface ShelfPhysics {
  body: RAPIER.RigidBody;
  /** Identity transform recorded at construction so reset() can right
   * the shelf back upright if the cat tipped it over. */
  init: {
    pos: { x: number; y: number; z: number };
    rot: { x: number; y: number; z: number; w: number };
  };
}

export function buildShelf(
  world: RAPIER.World,
  scene: SceneWorld,
): ShelfPhysics {
  const rowYs = scene.shelf.userData.rowYs as number[];
  const shelfWidth = scene.shelf.userData.rowWidth as number;
  const bottomBoardY = rowYs[0];
  const gapHeight = bottomBoardY - SHELF_THICKNESS / 2;
  const fillerHalfWidth = shelfWidth / 2 - 0.2;

  const body = world.createRigidBody(
    RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(0, 0, 0)
      .setLinearDamping(0.5)
      .setAngularDamping(0.5)
      .setCanSleep(true)
      .setCcdEnabled(true),
  );
  for (const y of rowYs) {
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(
        shelfWidth / 2,
        SHELF_THICKNESS / 2,
        SHELF_DEPTH / 2,
      )
        .setTranslation(0, y, 0)
        .setMass(8)
        .setFriction(0.85)
        .setRestitution(0.18),
      body,
    );
  }
  // Invisible filler under the bottom shelf — closes the trap zone
  // where cups would otherwise wedge between the bottom-shelf bottom
  // face and the floor (a gap shorter than a cup is tall).
  world.createCollider(
    RAPIER.ColliderDesc.cuboid(fillerHalfWidth, gapHeight / 2, SHELF_DEPTH / 2)
      .setTranslation(0, gapHeight / 2, 0)
      .setMass(4)
      .setFriction(0.85),
    body,
  );
  // Side supports.
  for (const x of [-shelfWidth / 2 + 0.1, shelfWidth / 2 - 0.1]) {
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(0.09, 1.7, SHELF_DEPTH / 2)
        .setTranslation(x, 1.7, 0)
        .setMass(6)
        .setFriction(0.85),
      body,
    );
  }

  return {
    body,
    init: {
      pos: { x: 0, y: 0, z: 0 },
      rot: { x: 0, y: 0, z: 0, w: 1 },
    },
  };
}
